import csv from 'csvtojson';

const aliases: Record<string, string> = {
  facebook: 'facebook_id',
  instagram: 'instagram_id',
  street: 'addr_line_1',
  city: 'addr_city',
  state: 'addr_state',
  zip: 'addr_zip',
  postal_code: 'addr_zip',
  country: 'addr_country',
};
const fields = new Set([
  'name',
  'address',
  'phone',
  'website',
  'facebook_id',
  'instagram_id',
  'addr_line_1',
  'addr_line_2',
  'addr_city',
  'addr_state',
  'addr_zip',
  'addr_country',
]);

export interface VenueImportRow {
  row: number;
  venue: Record<string, string>;
  error?: string;
  status: 'ready' | 'invalid' | 'saved' | 'failed';
}

export async function parseVenueCsv(text: string): Promise<VenueImportRow[]> {
  const records: string[][] = await csv({
    noheader: true,
    output: 'csv',
    trim: true,
  }).fromString(text.replace(/^\uFEFF/, ''));
  const headers = (records.shift() || []).map((value) => {
    const key = value
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return aliases[key] || key;
  });
  if (
    !headers.includes('name') ||
    (!headers.includes('address') && !headers.includes('addr_line_1'))
  ) {
    throw new Error(
      'Include name and address columns, or name and separate street, city, state, zip, country columns.'
    );
  }
  if (new Set(headers).size !== headers.length) {
    throw new Error(
      'Column names must be unique (including aliases such as facebook and facebook_id).'
    );
  }
  const seen = new Set<string>();
  const rows = records.map((values, index): VenueImportRow => {
    const venue: Record<string, string> = {};
    headers.forEach((key, column) => {
      if (fields.has(key)) venue[key] = (values[column] || '').trim();
    });
    let error =
      values.length !== headers.length
        ? 'Column count does not match the header.'
        : '';
    if (!venue.name) error = 'Name is required.';
    if (venue.address && !venue.addr_line_1) {
      const match = venue.address.match(
        /^(.+),\s*([^,]+),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)(?:,\s*(?:USA|US|United States))?$/i
      );
      if (match) {
        [
          ,
          venue.addr_line_1,
          venue.addr_city,
          venue.addr_state,
          venue.addr_zip,
        ] = match;
        venue.addr_state = venue.addr_state.toUpperCase();
        venue.addr_country = venue.addr_country || 'United States';
      } else {
        error =
          'Use a US address like "123 Main St, Madison, WI 53703", or separate street, city, state, zip, country columns.';
      }
    }
    delete venue.address;
    if (!venue.addr_line_1 || !venue.addr_city || !venue.addr_country) {
      error = error || 'Street, city and country are required.';
    }
    if (venue.website) {
      try {
        const url = new URL(venue.website);
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error();
      } catch {
        error = 'Website must be a valid http:// or https:// URL.';
      }
    }
    const identity = [
      venue.name,
      venue.addr_line_1,
      venue.addr_city,
      venue.addr_state,
      venue.addr_zip,
      venue.addr_country,
    ]
      .map((value) => (value || '').toLowerCase().replace(/\s+/g, ' ').trim())
      .join('|');
    if (seen.has(identity)) error = 'Duplicate venue in this file.';
    if (!error) seen.add(identity);
    return {
      row: index + 2,
      venue,
      error,
      status: error ? 'invalid' : 'ready',
    };
  });
  if (!rows.length) throw new Error('The CSV contains no venues.');
  if (rows.length > 1000)
    throw new Error('Import up to 1,000 venues at a time.');
  return rows;
}
